import React from 'react';
import { Card, CardContent } from './ui/card';
import { TrendingUp, TrendingDown, Minus, LucideIcon } from 'lucide-react';
import { cn } from '../lib/utils';

interface MetricCardProps {
  title: string;
  value: number | string;
  subtitle?: string;
  icon?: LucideIcon;
  trend?: 'up' | 'down' | 'neutral';
  trendValue?: string;
  colorScheme?: 'blue' | 'green' | 'purple' | 'amber' | 'indigo' | 'red' | 'gray';
  variant?: 'default' | 'neo';
}

const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  subtitle,
  icon: Icon,
  trend,
  trendValue,
  colorScheme = 'blue',
  variant = 'default'
}) => {
  const colorSchemes = {
    blue: 'text-blue-600 bg-blue-50',
    green: 'text-emerald-600 bg-emerald-50',
    purple: 'text-purple-600 bg-purple-50',
    amber: 'text-amber-600 bg-amber-50',
    indigo: 'text-indigo-600 bg-indigo-50',
    red: 'text-red-600 bg-red-50',
    gray: 'text-gray-600 bg-gray-50',
  };

  const getTrendIcon = () => {
    if (trend === 'up') return TrendingUp;
    if (trend === 'down') return TrendingDown;
    return Minus;
  };

  const getTrendColor = () => {
    if (trend === 'up') return 'text-emerald-600';
    if (trend === 'down') return 'text-red-600';
    return 'text-gray-500';
  };

  const TrendIcon = getTrendIcon();

  return (
    <Card 
      className={cn(
        "group relative overflow-hidden transition-all duration-200 h-full",
        variant === 'default' && "hover:shadow-md border-gray-200",
        variant === 'neo' && "border-2 border-[#161E54] shadow-neo rounded-none hover:translate-x-[-2px] hover:translate-y-[-2px] hover:shadow-neo-lg"
      )}
    >
      <CardContent className="p-5 sm:p-5 min-h-[130px] flex flex-col">
        <div className="flex items-center gap-3 mb-3">
          {Icon && (
            <div className={cn(
              "p-2 rounded-lg",
              colorSchemes[colorScheme],
              variant === 'neo' && "border border-[#161E54] rounded-none bg-white text-[#161E54]"
            )}>
              <Icon className="h-5 w-5" />
            </div>
          )}
          <h3 className={cn(
            "text-sm font-medium uppercase tracking-wide",
            variant === 'default' ? "text-gray-500" : "text-[#161E54] font-bold"
          )}>
            {title}
          </h3>
        </div>
        <div className="flex items-baseline gap-2 mb-1 flex-grow">
          <div className={cn(
            "text-4xl font-bold",
            variant === 'default' ? "text-gray-900" : "text-[#161E54]"
          )}>
            {value}
          </div>
          {trend && trendValue && (
            <div className={cn('flex items-center text-sm font-medium gap-0.5', getTrendColor())}>
              <TrendIcon className="h-4 w-4" />
              <span>{trendValue}</span>
            </div>
          )}
        </div>
        <div className={cn(
          "text-xs min-h-[16px]",
          variant === 'default' ? "text-gray-500" : "text-[#161E54]/70"
        )}>
          {subtitle || '\u00A0'}
        </div>
      </CardContent>
    </Card>
  );
};

export default MetricCard;

